import { TalkingAvatarComponent } from '../bots/talking-avatar/talking-avatar.component';
import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';

import { TBotItem } from '@core/interfaces/TBotItem';
import { GroupedResults, TResultsBot } from '@core/interfaces/TResultsItem';
import { ConversationRelativeDatePipe } from '@shared/pipes/conversation-relative-date.pipe';
import { HeaderComponent } from '@theme/header/header.component';

import { HistoryConversationService } from '../../../app/features/conversation/services/history-conversation.service';
import { AgentConversationComponent } from '../../features/conversation/components/agent-conversation.component';
import { ConversationDataService } from '../../features/conversation/services/conversation-data.service';
import { TalkingAvatarPlaylistComponent } from '../components/bots-messages/talking-avatar-playlist/talking-avatar-playlist.component';
import { ConfirmationModalComponent } from '../components/modals/confirmation-modal/confirmation-modal.component';
import { ResultDataLoaderService } from './result-data-loader.service';
import {
  ContentShellComponent,
  EmptyStateComponent,
  PanelHeaderComponent,
  PanelSearchComponent,
  PanelTabsComponent,
} from '@shared/components/content-panel';
import { BotsService } from '@shared/services/bots.service';
import { ResultsService } from '@shared/services/results.service';
import { resultsConversationRegex } from '@shared/utils/routing';

import { ResultsListComponent } from './results-list/results-list.component';

const LATEST_CONVERSATIONS_LIMIT = 12;

const GROUP_TAB_MAP: Record<string, 'byBot' | 'byDate' | 'byFavorites'> = {
  'By Bot': 'byBot',
  'By Date': 'byDate',
  'Favorites': 'byFavorites',
};

@Component({
  selector: 'app-results',
  templateUrl: './results.component.html',
  styleUrl: './results.component.scss',
  imports: [
    ConversationRelativeDatePipe,
    MatProgressSpinner,
    MatTooltip,
    ResultsListComponent,
    AgentConversationComponent,
    TalkingAvatarPlaylistComponent,
    TalkingAvatarComponent,
    ContentShellComponent,
    EmptyStateComponent,
    HeaderComponent,
    PanelHeaderComponent,
    PanelSearchComponent,
    PanelTabsComponent,
  ],
})
export class ResultsComponent implements OnInit, OnDestroy {
  protected canSelectResults = false;
  protected selectedOption: 'byDate' | 'byBot' | 'byFavorites' = 'byBot';
  protected selectedItem: TResultsBot | null = null;
  protected latestConversations: TResultsBot[] = [];
  protected loadingLatest = false;
  protected searchQuery = '';
  /** True until history API finishes for the selected conversation (covers chat until messages are ready). */
  protected conversationHistoryLoading = signal(false);
  protected bot: TBotItem | undefined;
  protected botLookupLoading = false;
  protected botUnavailableReason: string | null = null;

  get filteredGroupedResults(): GroupedResults[] {
    const groups = this.resultDataLoaderService.groupedResults();
    if (this.selectedOption !== 'byBot' || !this.searchQuery.trim()) {
      return groups;
    }
    const q = this.searchQuery.toLowerCase();
    return groups.filter(g => g.name.toLowerCase().includes(q));
  }

  groupTabs: string[] = ['By Bot', 'By Date', 'Favorites'];
  activeGroupTab = 'By Bot';

  private _subs: Subscription[] = [];
  private _botLookupSub: Subscription | null = null;
  private _originBotId: string | null = null;
  private multipleItemsSelected: TResultsBot[] = [];
  private _removeUrlChangeListener: VoidFunction | null = null;

  constructor(
    private _resultsService: ResultsService,
    protected resultDataLoaderService: ResultDataLoaderService,
    private _location: Location,
    protected dialog: MatDialog,
    private _historyConversationService: HistoryConversationService,
    private _botsService: BotsService,
    private _conversationDataService: ConversationDataService
  ) {
    this._removeUrlChangeListener = this._location.onUrlChange(() => this.goToResultByUrl());
  }

  hideSingleSelectionIndicator() { return signal(false); }

  ngOnInit(): void {
    this.goToResultByUrl();
    this._subs.push(
      this._conversationDataService.conversation$.subscribe(conversation => {
        if (!conversation || conversation.id !== this.selectedItem?._id) {
          return;
        }
        this.loadOriginBot(conversation?.bot?._id);
      })
    );

    // `ResultDataLoaderService` is a singleton: grouped data can be from another tab (By Date /
    // Favorites) while this instance defaults to By Bot. Always reload for the active tab.
    this.loadGroups();
    this.loadLatestConversations();
  }

  onGroupTabChange(tab: string): void {
    this.activeGroupTab = tab;
    this.selectedOption = GROUP_TAB_MAP[tab] ?? 'byBot';
    this.loadGroups();
  }

  protected loadLatestConversations(): void {
    this.loadingLatest = true;
    const sub = this._resultsService
      .fetchResults(1, LATEST_CONVERSATIONS_LIMIT)
      .subscribe({
        next: res => {
          this.latestConversations = res.results;
          this.loadingLatest = false;
        },
        error: () => {
          this.loadingLatest = false;
        },
      });
    this._subs.push(sub);
  }

  loadGroups(): void {
    switch (this.selectedOption) {
      case 'byDate':
        this.resultDataLoaderService.loadDateGroups();
        break;
      case 'byBot':
        this.resultDataLoaderService.loadBotGroups();
        break;
      case 'byFavorites':
        this.resultDataLoaderService.loadFavoritesGroups();
        break;
    }
  }

  toggleSelectAll(): void {
    this.canSelectResults = !this.canSelectResults;
  }

  protected onConversationHistoryLoading(loading: boolean): void {
    this.conversationHistoryLoading.set(loading);
  }

  setSelectedItem(item: TResultsBot): void {
    const isSameConversation = this.selectedItem?._id === item._id;
    this.conversationHistoryLoading.set(true);
    this.selectedItem = item;
    this.loadOriginBot(item.botID);

    if (isSameConversation) {
      // Same `conversationId` input value does not retrigger the child loader — refresh history here and clear the overlay.
      this._historyConversationService.getConversationById(
        item._id,
        () => this.conversationHistoryLoading.set(false),
        () => this.conversationHistoryLoading.set(false)
      );
    }

    this._location.go(`/results/conversation/${item._id}`);
  }

  openDeleteMultipleModal(): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        text: `Are you sure you want remove the selected results?`,
      },
    });
    const dialogSubscription = dialogRef.componentInstance.actionCallback.subscribe(() => {
      const ids = this.multipleItemsSelected.map(i => i._id as string);
      this._resultsService.deleteMultiple(ids);
      dialogRef.close();

      ids.forEach(id => {
        this.resultDataLoaderService.removeConversationFromGroups(id);
        this.onConversationDeleted(id);
      });

      this.multipleItemsSelected = [];
      this.canSelectResults = false;
      dialogSubscription.unsubscribe();
    });
  }

  ngOnDestroy(): void {
    this._botLookupSub?.unsubscribe();
    this._subs.forEach(sub => sub.unsubscribe());
    this._removeUrlChangeListener?.();
    this._historyConversationService.cancelActiveRequest();
    this._conversationDataService.reset();
  }

  multipleItemsSelectedHandler(event: { checked: boolean; item: TResultsBot }): void {
    if (event.checked) {
      this.multipleItemsSelected.push(event.item);
    } else {
      this.multipleItemsSelected = this.multipleItemsSelected.filter(
        item => item._id !== event.item._id
      );
    }
  }

  onConversationDeleted(conversationId: string): void {
    if (this.selectedItem?._id === conversationId) {
      this.selectedItem = null;
      this.conversationHistoryLoading.set(false);
      this.resetOriginBotState();
      this._location.replaceState('/results');
      this.loadLatestConversations();
    }
    this.multipleItemsSelected = this.multipleItemsSelected.filter(
      item => item._id !== conversationId
    );
    this.latestConversations = this.latestConversations.filter(
      item => item._id !== conversationId
    );
  }

  private goToResultByUrl = (): void => {
    const path = this._location.path();
    // Leaving /results: keep current UI until destroy — clearing here flashes empty state (OAU-330).
    if (!path.startsWith('/results')) {
      return;
    }

    const pathMatch = path.match(resultsConversationRegex);

    if (pathMatch) {
      const conversationId = pathMatch[1];
      if (!this.selectedItem?._id || this.selectedItem?._id !== conversationId) {
        this.conversationHistoryLoading.set(true);
        this.selectedItem = { _id: conversationId } as TResultsBot;
      }
    } else {
      this.selectedItem = null;
      this.conversationHistoryLoading.set(false);
      this.resetOriginBotState();
    }
  };

  private loadOriginBot(botId: string | null | undefined): void {
    const normalizedBotId = botId ?? null;
    const lookupAlreadyResolved =
      this._originBotId === normalizedBotId &&
      (this.botLookupLoading || !!this.bot || !!this.botUnavailableReason);

    if (lookupAlreadyResolved) {
      return;
    }

    this._botLookupSub?.unsubscribe();
    this._originBotId = normalizedBotId;
    this.bot = undefined;
    this.botUnavailableReason = null;
    this.botLookupLoading = false;

    if (!normalizedBotId) {
      this.botUnavailableReason = this.getMissingBotMessage();
      return;
    }

    this.botLookupLoading = true;
    this._botLookupSub = this._botsService
      .fetchBotById(normalizedBotId, { suppressNotFoundRedirect: true })
      .subscribe({
        next: bot => {
          if (this._originBotId !== normalizedBotId) {
            return;
          }
          this.bot = bot;
          this.botUnavailableReason = null;
          this.botLookupLoading = false;
        },
        error: err => {
          if (this._originBotId !== normalizedBotId) {
            return;
          }
          this.bot = undefined;
          this.botLookupLoading = false;
          this.botUnavailableReason = err?.status === 404
            ? this.getMissingBotMessage()
            : 'Agent details could not be loaded. You can view this saved result, but continuing the conversation is temporarily unavailable.';
          if (err?.status !== 404) {
            console.error(err);
          }
        },
      });
  }

  private resetOriginBotState(): void {
    this._botLookupSub?.unsubscribe();
    this._botLookupSub = null;
    this._originBotId = null;
    this.bot = undefined;
    this.botLookupLoading = false;
    this.botUnavailableReason = null;
  }

  private getMissingBotMessage(): string {
    return 'This agent no longer exists. You can view this saved result, but the conversation cannot be continued.';
  }
}
